/**
 * Sector operations — Clinics client service (Cold Chain, Starters, BMW).
 * R39 M6.b + M7.b (plan docs/plans/sector-operations.md §2 SO-P258).
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { firebaseStorage, firebaseAuth, firestoreDb } from './firebase'
import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import {
  ALLOWED_PHOTO_MIME_TYPES,
  MAX_PHOTO_BYTES,
} from './opsRecordPhotosService'
import { getNlsString } from '../nls/nlsRuntime'
import type {
  EnsureClinicsStarterSetInput,
  EnsureClinicsStarterSetResult,
  ImportTempLogInput,
  ImportTempLogResult,
  CreateSampleBagInput,
  AttachColdBoxPhotoInput,
  StartSampleHandoverInput,
  StartSampleHandoverResult,
  GetSampleHandoverInfoInput,
  GetSampleHandoverInfoResult,
  PrepareSampleHandoverOtpInput,
  PrepareSampleHandoverOtpResult,
  ConfirmSampleHandoverInput,
  ConfirmSampleHandoverResult,
  ReceiveSamplesAtLabInput,
  ReceiveSamplesAtLabResult,
  CreateHomeCollectionJobInput,
  CreateHomeCollectionJobResult,
  GetHomeCollectionAddressInput,
  GetHomeCollectionAddressResult,
  RecordRunnerPingInput,
  RecordRunnerPingResult,
  SampleBag,
  SampleBagStage,
  SampleSettingsDoc,
  SampleRunner,
  CreateNarcItemInput,
  ArchiveNarcItemInput,
  RecordNarcMoveInput,
  StartNarcCountInput,
  SubmitNarcTallyInput,
  GetRxPhotoUrlInput,
  ExportNarcRegisterInput,
  OpsCliNarcItem,
  OpsCliNarcMove,
  OpsCliNarcCount,
  OpsCliNarcCountState,
  GetHubSpokeScoresResult,
  OpsCliJobOrder,
  OpsCliJobOrderStage,
  CreateJobOrderInput,
  UpdateJobOrderStageInput,
  OpsCliDialyzer,
  RegisterDialyzerInput,
  RecordDialyzerUseInput,
  OpsCliIvfWitness,
  OpsCliIvfWitnessStatus,
  CreateIvfWitnessStepInput,
  ConfirmIvfWitnessStepInput,
  OpsCliTeleSession,
  OpsCliTeleSessionSummary,
  OpsCliTeleConsentState,
  CreateTeleSessionInput,
  GetTeleSessionPublicInfoInput,
  GetTeleSessionPublicInfoResult,
  PrepareTeleConsentOtpInput,
  PrepareTeleConsentOtpResult,
  SubmitTeleConsentInput,
  StartTeleSessionInput,
  CreateTeleOrderInput,
  CreateTeleOrderKind,
} from '../types/ops'

export async function ensureClinicsStarterSet(
  input: EnsureClinicsStarterSetInput,
): Promise<EnsureClinicsStarterSetResult> {
  return callable<EnsureClinicsStarterSetInput, EnsureClinicsStarterSetResult>(
    'ensureClinicsStarterSet',
    input,
  )
}

export async function importTempLog(
  input: ImportTempLogInput,
): Promise<ImportTempLogResult> {
  return callable<ImportTempLogInput, ImportTempLogResult>(
    'importTempLog',
    input,
  )
}

export async function createSampleBag(
  input: CreateSampleBagInput,
): Promise<{ bagId: string; stage: SampleBagStage; barcodesCount: number }> {
  return callable('createSampleBag', input)
}

export async function attachColdBoxPhoto(
  input: AttachColdBoxPhotoInput,
): Promise<{ success: boolean }> {
  return callable('attachColdBoxPhoto', input)
}

const EXTENSION_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
}

function getSafeExtension(file: File): string {
  const nameExt = file.name.split('.').pop()?.toLowerCase()
  if (nameExt && ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(nameExt)) {
    return nameExt === 'jpeg' ? 'jpg' : nameExt
  }
  return EXTENSION_MAP[file.type] || 'jpg'
}

export async function uploadSampleColdBoxPhoto(
  workspaceId: string,
  bagId: string,
  file: File,
): Promise<{ downloadUrl: string; path: string }> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!bagId) throw new Error('bagId is required.')

  if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_cli_sample_bags/${bagId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)

  await attachColdBoxPhoto({
    workspaceId,
    bagId,
    photoPath: path,
  })

  return { downloadUrl, path }
}

export async function startSampleHandover(
  input: StartSampleHandoverInput,
): Promise<StartSampleHandoverResult> {
  return callable('startSampleHandover', input)
}

export async function getSampleHandoverInfo(
  input: GetSampleHandoverInfoInput,
): Promise<GetSampleHandoverInfoResult> {
  return callable('getSampleHandoverInfo', input)
}

export async function prepareSampleHandoverOtp(
  input: PrepareSampleHandoverOtpInput,
): Promise<PrepareSampleHandoverOtpResult> {
  return callable('prepareSampleHandoverOtp', input)
}

export async function confirmSampleHandover(
  input: ConfirmSampleHandoverInput,
): Promise<ConfirmSampleHandoverResult> {
  return callable('confirmSampleHandover', input)
}

export async function receiveSamplesAtLab(
  input: ReceiveSamplesAtLabInput,
): Promise<ReceiveSamplesAtLabResult> {
  return callable('receiveSamplesAtLab', input)
}

export async function setSampleSettings(
  input: { workspaceId: string; stageLimitsMinutes?: Record<string, number>; runners?: SampleRunner[] },
): Promise<{ success: boolean }> {
  return callable('setSampleSettings', input)
}

export async function getSampleSettings(
  workspaceId: string,
): Promise<SampleSettingsDoc> {
  return callable('getSampleSettings', { workspaceId })
}

export async function listSampleBags(
  workspaceId: string,
  stage?: SampleBagStage,
): Promise<SampleBag[]> {
  return callable('listSampleBags', { workspaceId, stage })
}

export async function getSampleBag(
  workspaceId: string,
  bagId: string,
): Promise<SampleBag> {
  return callable('getSampleBag', { workspaceId, bagId })
}

export async function createHomeCollectionJob(
  input: CreateHomeCollectionJobInput,
): Promise<CreateHomeCollectionJobResult> {
  return callable('createHomeCollectionJob', input)
}

export async function getHomeCollectionAddress(
  input: GetHomeCollectionAddressInput,
): Promise<GetHomeCollectionAddressResult> {
  return callable('getHomeCollectionAddress', input)
}

export async function recordRunnerPing(
  input: RecordRunnerPingInput,
): Promise<RecordRunnerPingResult> {
  return callable('recordRunnerPing', input)
}

// ---------------------------------------------------------------------------
// Narcotics Vault & Ledger (R39 M9)
// ---------------------------------------------------------------------------

export async function createNarcItem(
  input: CreateNarcItemInput,
): Promise<{ itemId: string; item: OpsCliNarcItem }> {
  return callable('createNarcItem', input)
}

export async function archiveNarcItem(
  input: ArchiveNarcItemInput,
): Promise<{ itemId: string; archived: boolean }> {
  return callable('archiveNarcItem', input)
}

export async function recordNarcMove(
  input: RecordNarcMoveInput,
): Promise<{ moveId: string; balanceAfter: number; move: OpsCliNarcMove; replayed?: boolean }> {
  return callable('recordNarcMove', input)
}

export async function startNarcCount(
  input: StartNarcCountInput,
): Promise<{ countId: string; count: OpsCliNarcCount }> {
  return callable('startNarcCount', input)
}

export async function submitNarcTally(
  input: SubmitNarcTallyInput,
): Promise<{ countId: string; state: OpsCliNarcCountState; requestId?: string }> {
  return callable('submitNarcTally', input)
}

export async function listNarcItems(
  workspaceId: string,
  vaultAssetId?: string,
): Promise<OpsCliNarcItem[]> {
  return callable('listNarcItems', { workspaceId, vaultAssetId })
}

export async function listNarcMoves(
  workspaceId: string,
  vaultAssetId?: string,
  itemId?: string,
): Promise<OpsCliNarcMove[]> {
  return callable('listNarcMoves', { workspaceId, vaultAssetId, itemId })
}

export async function listNarcCounts(
  workspaceId: string,
  vaultAssetId?: string,
): Promise<OpsCliNarcCount[]> {
  return callable('listNarcCounts', { workspaceId, vaultAssetId })
}

export async function getRxPhotoUrl(
  input: GetRxPhotoUrlInput,
): Promise<{ url: string; expiresAt: number }> {
  return callable('getRxPhotoUrl', input)
}

export async function exportNarcRegister(
  input: ExportNarcRegisterInput,
): Promise<{ csv: string; count: number }> {
  return callable('exportNarcRegister', input)
}

export async function uploadNarcRxPhoto(
  workspaceId: string,
  moveId: string,
  file: File,
): Promise<{ downloadUrl: string; path: string }> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!moveId) throw new Error('moveId is required.')

  if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_cli_rx/${moveId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)
  return { downloadUrl, path }
}

export async function listWorkspaceMembers(
  workspaceId: string,
): Promise<Array<{ uid: string; name: string; role?: string; status?: string }>> {
  if (!workspaceId) return []
  const membersRef = collection(firestoreDb, 'workspace_members')
  const q = query(membersRef, where('workspaceId', '==', workspaceId))
  const snap = await getDocs(q)
  return snap.docs.map((docSnap) => {
    const data = docSnap.data()
    return {
      uid: data.uid || docSnap.id.replace(`${workspaceId}_`, ''),
      name: data.displayName || data.name || data.email || 'Member',
      role: data.role,
      status: data.status,
    }
  })
}

export async function getHubSpokeScores(
  workspaceId: string,
  hubPlaceId: string,
  days: 7 | 30 = 7,
): Promise<GetHubSpokeScoresResult> {
  return callable('getHubSpokeScores', { workspaceId, hubPlaceId, days })
}

// ---------------------------------------------------------------------------
// Specialty Loops (R39 M17)
// ---------------------------------------------------------------------------

export async function listJobOrders(
  workspaceId: string,
  stage?: OpsCliJobOrderStage,
): Promise<OpsCliJobOrder[]> {
  return callable('listJobOrders', { workspaceId, stage })
}

export async function createJobOrder(
  input: CreateJobOrderInput,
): Promise<{ orderId: string; order: OpsCliJobOrder }> {
  return callable('createJobOrder', input)
}

export async function updateJobOrderStage(
  input: UpdateJobOrderStageInput,
): Promise<{ orderId: string; stage: OpsCliJobOrderStage; remakeCount: number }> {
  return callable('updateJobOrderStage', input)
}

export async function listDialyzers(
  workspaceId: string,
  patientToken?: string,
): Promise<OpsCliDialyzer[]> {
  return callable('listDialyzers', { workspaceId, patientToken })
}

export async function registerDialyzer(
  input: RegisterDialyzerInput,
): Promise<{ dialyzerId: string; dialyzer: OpsCliDialyzer }> {
  return callable('registerDialyzer', input)
}

export async function recordDialyzerUse(
  input: RecordDialyzerUseInput,
): Promise<{ dialyzerId: string; useCount: number; discarded: boolean }> {
  return callable('recordDialyzerUse', input)
}

export async function listIvfWitnessQueue(
  workspaceId: string,
): Promise<OpsCliIvfWitness[]> {
  return callable('listIvfWitnessQueue', { workspaceId })
}

export async function createIvfWitnessStep(
  input: CreateIvfWitnessStepInput,
): Promise<{ stepId: string; step: OpsCliIvfWitness }> {
  return callable('createIvfWitnessStep', input)
}

export async function confirmIvfWitnessStep(
  input: ConfirmIvfWitnessStepInput,
): Promise<{ stepId: string; status: OpsCliIvfWitnessStatus; requestId?: string }> {
  return callable('confirmIvfWitnessStep', input)
}

export async function uploadJobOrderPhoto(
  workspaceId: string,
  orderId: string,
  file: File,
): Promise<{ downloadUrl: string; path: string }> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!orderId) throw new Error('orderId is required.')

  if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_cli_job_orders/${orderId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)
  return { downloadUrl, path }
}

// ---------------------------------------------------------------------------
// Teleconsultation (R39 M18)
// ---------------------------------------------------------------------------

export async function createTeleSession(
  input: CreateTeleSessionInput,
): Promise<{ sessionId: string; linkToken: string; session: OpsCliTeleSession }> {
  return callable('createTeleSession', input)
}

export async function getTeleSessionPublicInfo(
  input: GetTeleSessionPublicInfoInput,
): Promise<GetTeleSessionPublicInfoResult> {
  return callable('getTeleSessionPublicInfo', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function prepareTeleConsentOtp(
  input: PrepareTeleConsentOtpInput,
): Promise<PrepareTeleConsentOtpResult> {
  return callable('prepareTeleConsentOtp', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function submitTeleConsent(
  input: SubmitTeleConsentInput,
): Promise<{ sessionId: string; consentState: OpsCliTeleConsentState }> {
  return callable('submitTeleConsent', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function startTeleSession(
  input: StartTeleSessionInput,
): Promise<{ sessionId: string; startedAt: number }> {
  return callable('startTeleSession', input)
}

export async function createTeleOrder(
  input: CreateTeleOrderInput,
): Promise<{ orderId: string; kind: CreateTeleOrderKind; status: string }> {
  return callable('createTeleOrder', input)
}

export async function listTeleSessions(
  workspaceId: string,
): Promise<OpsCliTeleSessionSummary[]> {
  return callable('listTeleSessions', { workspaceId })
}


